import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Star, MapPin, BedDouble, X } from "lucide-react";
import PayButton from "../components/PayButton";
import useGeo from "../hooks/useGeo";
import GeoToggle from "../components/GeoToggle";

export default function Hostels() {
  const [items, setItems] = useState([]);
  const [reserve, setReserve] = useState(null);
  const geo = useGeo(25);
  useEffect(() => { axios.get(`${API}/hostels`, { params: geo.geoParams }).then(r => setItems(r.data)); }, [geo.mode, geo.coords]);

  return (
    <div className="space-y-6" data-testid="hostels-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold">Pet Hostels</h1>
          <p className="text-ink-soft">Travelling? Leave your pet in caring hands.</p>
        </div>
        <GeoToggle geo={geo} label="hostels" />
      </div>
      {geo.isNearby && items.length === 0 && (
        <div className="bg-surface-muted border border-line rounded-2xl p-6 text-center text-sm" data-testid="hostels-empty-nearby">
          <div className="font-bold mb-1">No hostels within {geo.radius} km of you yet.</div>
          <p className="text-ink-soft">Switch to <button onClick={() => geo.setMode("all")} className="underline font-bold">All India</button> to see partner hostels everywhere.</p>
        </div>
      )}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map(h => (
          <article key={h.hostel_id} data-testid={`hostel-${h.hostel_id}`} className="bg-surface border border-line rounded-3xl overflow-hidden shadow-soft hover:-translate-y-1 hover:shadow-hover transition-all">
            <img src={h.photo} alt={h.name} className="w-full h-40 object-cover" />
            <div className="p-5">
              <div className="flex justify-between items-start gap-2">
                <div>
                  <h3 className="font-heading text-xl font-extrabold">{h.name}</h3>
                  <div className="text-xs text-ink-soft flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" />{h.city}{h.distance_km != null && <span className="ml-1 font-bold text-brand">· {h.distance_km} km</span>}</div>
                </div>
                <div className="flex items-center gap-1 text-sm font-bold"><Star className="w-4 h-4 fill-sunshine text-sunshine"/>{h.rating}</div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {h.amenities.map(a => <span key={a} className="text-[11px] uppercase tracking-wider font-bold bg-surface-muted rounded-full px-2 py-1">{a}</span>)}
              </div>
              <div className="mt-4 flex items-center justify-between">
                <div className="text-sm text-ink-soft flex items-center gap-1.5"><BedDouble className="w-3.5 h-3.5" />{h.available}/{h.capacity} available</div>
                <div className="font-heading font-extrabold text-lg">₹{h.price_per_day}<span className="text-xs font-bold text-ink-soft">/day</span></div>
              </div>
              <button data-testid={`book-hostel-${h.hostel_id}`} onClick={() => setReserve({ hostel: h, days: 3 })} className="mt-4 w-full bg-ink text-white rounded-full py-2.5 text-sm font-bold hover:bg-brand transition-colors">Reserve</button>
            </div>
          </article>
        ))}
      </div>
      {reserve && <ReserveModal reserve={reserve} setReserve={setReserve} onClose={() => setReserve(null)}/>}
    </div>
  );
}

function ReserveModal({ reserve, setReserve, onClose }) {
  const { hostel, days } = reserve;
  const [paid, setPaid] = useState(false);
  const total = hostel.price_per_day * days;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-surface rounded-3xl p-6 w-full max-w-md border border-line shadow-soft" data-testid="hostel-reserve-modal">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-xl font-extrabold">Reserve at {hostel.name}</h3>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-surface-muted"><X className="w-4 h-4"/></button>
        </div>
        {!paid ? (
          <>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-wider text-ink-soft">Number of days</span>
              <input data-testid="hostel-days" type="number" min="1" max="60" value={days} onChange={e => setReserve({ ...reserve, days: Math.max(1, Number(e.target.value)) })} className="w-full mt-1 bg-surface-muted border border-line rounded-full px-4 py-2 font-bold outline-none focus:border-brand"/>
            </label>
            <div className="mt-4 flex justify-between bg-surface-muted rounded-2xl p-3">
              <span className="font-bold">{days} day × ₹{hostel.price_per_day}</span>
              <span className="font-heading font-extrabold">₹{total}</span>
            </div>
            <div className="mt-4">
              <PayButton
                amount={total}
                currency="INR"
                country="IN"
                purpose="hostel_booking"
                referenceId={hostel.hostel_id}
                label={`Pay ₹${total} & reserve`}
                dataTestid="hostel-pay-button"
                onSuccess={() => setPaid(true)}
              />
            </div>
          </>
        ) : (
          <div className="text-center py-6">
            <BedDouble className="w-12 h-12 text-leaf mx-auto mb-2"/>
            <div className="font-heading text-xl font-extrabold">Reservation confirmed!</div>
            <p className="text-sm text-ink-soft mt-1">{hostel.name} will be in touch to coordinate drop-off.</p>
            <button onClick={onClose} className="mt-4 bg-ink text-white rounded-full px-5 py-2 text-sm font-bold">Close</button>
          </div>
        )}
      </div>
    </div>
  );
}
